import React, { useState, useCallback } from "react";
import { SaveEditFormResponse, EditFormElement } from "@kaltura/mediaspace-shared-edit-entry";
import { noop, translate } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { ModalActions, ModalTitle, Button } from "@kaltura/ds-react-components";
import AddFromUrlEditForm from "./AddFromUrlEditForm";
import { StyledModalContent } from "./StyleComponents";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { PostSaveComponent, PreSaveComponent } from "@kaltura/mediaspace-shared-upload";
import { KalturaMediaEntry } from "kaltura-typescript-client/api/types/KalturaMediaEntry";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

interface AddFromUrlEditModalProps {
    /** Controls modal visibility. */
    open: boolean;

    /** Callback for cancel action from the parent (closing the modal). */
    onCancel: () => void;

    /** The entry imported from URL to edit. */
    entry: KalturaMediaEntry;

    /**
     * Callback when the modal is closed.
     * If `shouldRedirect` is true/omitted, the parent may redirect to the entry page.
     */
    onClose?: (shouldRedirect?: boolean) => void;

    /** Structure of editable form fields. */
    fieldsStructure: EditFormElement[];

    /** Optional KMS components rendered sequentially before saving to confirm or adjust values. */
    preSaveComponents?: KmsTypeComponent[];

    /** Optional KMS component rendered after a successful save. */
    postSaveComponent?: KmsTypeComponent;

    /** Enables or disables publish functionality (default: true). */
    publishEnabled?: boolean;

    /** Whether to show the media link in the edit form (default: true). */
    showMediaLink?: boolean;

    /** Whether to show the footer action buttons (default: true). */
    showEditFormButtons?: boolean;

    /** Whether to render the modal title header (default: true). */
    showTitle?: boolean;

    /** Optional HTML form id to use for the inner form element. */
    formId?: string;

    /**
     * Callback notified whenever the inner form can or cannot be saved.
     * Used to enable/disable the “Save & Close” button.
     */
    onCanSaveForm?: (canSaveForm: boolean) => void;

    /** Callback when the entry becomes ready (or not) for publish. */
    onReadyForPublish?: (entryIds: string[], readyForPublish: boolean) => void;

    /** Callback when form submit starts (before the save request completes). */
    onSubmitStarted?: () => void;

    /** Callback when the form is submitted and saved successfully. */
    onSubmit?: (entry: KalturaMediaEntry) => void;
}

/*
 * AddFromUrlEditModal is a modal form component for editing metadata of an entry imported from a URL.
 * It manages the state and logic for the edit form, and delegates the UI rendering to AddFromUrlEditForm.
 * The form handles save/cancel actions, shows processing state, and displays toast notifications on completion.
 */
export const AddFromUrlEditModal: React.FC<AddFromUrlEditModalProps> = ({
    entry,
    onCancel,
    fieldsStructure,
    onClose,
    onCanSaveForm,
    onReadyForPublish,
    onSubmitStarted,
    onSubmit,
    publishEnabled = true,
    showMediaLink = true,
    showEditFormButtons = true,
    showTitle = true,
    formId = "add-from-url-edit-form",
    preSaveComponents,
    postSaveComponent,
}) => {
    const [editRowKey, setEditRowKey] = useState<number>(0);
    const [formProcessing, setFormProcessing] = useState<boolean>(false);
    const [canSaveForm, setCanSaveForm] = useState<boolean>(false);
    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();
    const [formData, setFormData] = useState<{ entryIds: string[]; data: any }>({ entryIds: [], data: {} });
    const [displayPreSaveComponent, setDisplayPreSaveComponent] = useState<boolean>(false);
    const [displayPostSaveComponent, setDisplayPostSaveComponent] = useState<boolean>(false);
    const [saveConfirmationResolvePromise, setSaveConfirmationResolvePromise] = useState<
        ((value: { confirm: boolean; isEditable: boolean }) => void) | null
    >(null);

    const showPreSaveComponent = useCallback(
        (entryIds: string[], values: any): Promise<{ confirm: boolean; isEditable: boolean }> => {
            return new Promise<{ confirm: boolean; isEditable: boolean }>((resolve) => {
                setFormData({ entryIds, data: values });
                setDisplayPreSaveComponent(true);
                setSaveConfirmationResolvePromise(() => resolve);
            });
        },
        []
    );

    const onBeforeSubmit = async (entryIds: string[], values: any) => {
        if (!preSaveComponents?.length) {
            return { confirm: true, isEditable: true };
        }

        const { confirm, isEditable } = await showPreSaveComponent(entryIds, values);

        return { confirm, isEditable };
    };

    const handleOnCanSaveForm = (canSave: boolean) => {
        setCanSaveForm(canSave);
        onCanSaveForm?.(canSave);
    };

    const handleSubmitStarted = () => {
        setFormProcessing(true);
        sendButtonAnalytics("Save & Close", ButtonClickAnalyticsType.SAVE);
        onSubmitStarted?.();
    };

    const handleSave = ({ error, result }: SaveEditFormResponse) => {
        if (error) {
            showToast({ severity: "error", message: translate("Failed to update information") });
        }
        else {
            showToast({ severity: "success", message: translate("Your information has been successfully updated.") });

            if (postSaveComponent) {
                setDisplayPostSaveComponent(true);
            }
        }

        // If we have postSaveComponent and there is no error, we will call to onClose only after the postSaveComponent is closed.
        // If there is no postSaveComponent or there is an error, we will call to onClose immediately.
        // We need this because if onClose performs a redirect to the entry page, we need to display the postSaveComponent before redirecting.
        if (!postSaveComponent || (postSaveComponent && error)) {
            onSubmit?.(entry);
            onClose?.(); // default shouldRedirect is true
        }
    };

    return (
        <>
            {showTitle && (
                <ModalTitle
                    classes={{ closeButton: "add-from-url-edit-modal-close" }}
                    closeButtonAriaLabel={translate("Close add from URL entry edit form")}
                >
                    {translate("Import from URL")}
                </ModalTitle>
            )}
            <StyledModalContent>
                <AddFromUrlEditForm
                    entry={entry}
                    fieldsStructure={fieldsStructure}
                    editRowKey={editRowKey}
                    onCancel={noop}
                    onEdit={noop}
                    onBeforeSubmit={onBeforeSubmit}
                    onSubmit={handleSave}
                    onSubmitStarted={handleSubmitStarted}
                    onRemove={() => onCancel()}
                    onClose={() => {
                        setEditRowKey((prevState) => prevState + 1);
                    }}
                    publishEnabled={publishEnabled}
                    showMediaLink={showMediaLink}
                    formId={formId}
                    showEditFormButtons={false}
                    onCanSaveForm={handleOnCanSaveForm}
                    onReadyForPublish={onReadyForPublish}
                />
            </StyledModalContent>

            {showEditFormButtons && (
                <ModalActions>
                    <Button
                        kuid={"edit-form-cancel"}
                        form={formId}
                        variant={"borderless"}
                        color={"secondary"}
                        onClick={() => onClose?.(false)}
                    >
                        {translate("Close")}
                    </Button>
                    <Button
                        kuid={"edit-form-save"}
                        form={formId}
                        variant={"pill"}
                        loading={formProcessing}
                        type={"submit"}
                        disabled={!canSaveForm}
                    >
                        {translate("Save & Close")}
                    </Button>
                </ModalActions>
            )}

            {!!preSaveComponents?.length && (
                <PreSaveComponent
                    kmsComponents={preSaveComponents}
                    display={displayPreSaveComponent}
                    formData={formData}
                    saveConfirmationResolvePromise={saveConfirmationResolvePromise}
                    setDisplayPreSaveComponent={setDisplayPreSaveComponent}
                />
            )}
            {postSaveComponent && (
                <PostSaveComponent
                    kmsComponent={postSaveComponent}
                    display={displayPostSaveComponent}
                    formData={formData}
                    onClose={() => {
                        setDisplayPostSaveComponent(false);
                        onSubmit?.(entry);
                        onClose?.(); // default shouldRedirect is true
                    }}
                />
            )}
        </>
    );
};

export default AddFromUrlEditModal;
